import { Box, Chip, FormControl, Autocomplete, TextField } from "@mui/material";
import React from "react";
import * as PropTypes from "prop-types";
import { isFunction } from "../../utils";
import { useTranslation } from "react-i18next";
import { CompTooltipIcon } from "../TooltipIcon";

const SearchMultiselectDropdown = (props) => {
    let { error } = props;
    const {
      rows = [],
      selectedRows = [],
      keyColumn,
      valueColumn,
      valueFunc = (row) => (row?.[valueColumn] ?? row).toString(),
      onSelect,
      disabled = false,
      label = "",
      parentsUnselectable = false
    } = props;
    
    //NOTE: TEST FUNCTIONALITY WELL BEFORE USING IN OTHER COMPONENTS IN PROD. CURRENTLY ONLY IN USE FOR THE NEW FUNCTIONS MODULE
    const { t } = useTranslation("translation");

    error = error !== undefined && error !== null ? t("errors." + error) : null;

    const handleChange = (event) => {
      const nextIds = event.target.value;
      const prevIds = selectedRows ?? [];

      const added = nextIds.filter((id) => !prevIds.includes(id));
      const removed = prevIds.filter((id) => !nextIds.includes(id));

      let toggledId, isAddAction;
      if (added.length) {
          toggledId = added[0];
          isAddAction = true;
      } else if (removed.length) {
          toggledId = removed[0];
          isAddAction = false;
      } else {
          return;
      }

      const row = rows.find((r) => r[keyColumn] === toggledId);
      if (!row) return;

      const index = rows.indexOf(row);
      const i = prevIds.indexOf(toggledId);

      if (isFunction(onSelect)) {
        onSelect(row, isAddAction, index, i);
      }
    };

    return (
      <FormControl
        className={`comp-multiselect-dropdown-container
          ${props.className ? "container-" + props.className : ""} 
          ${error ? "error" : ""} 
          ${disabled ? "disabled" : ""}`}
      >

        <Autocomplete
          multiple
          disabled={disabled}
          options={rows}
          disableCloseOnSelect
          value={rows.filter((row) => selectedRows.includes(row[keyColumn]))}
          onChange={(event, newValue) => {
            const selectedIds = newValue.map((row) => row[keyColumn]);
            handleChange({ target: { value: selectedIds } }); 
          }}
          isOptionEqualToValue={(option, value) => option[keyColumn] === value[keyColumn]}
          getOptionLabel={(option) => valueFunc(option).trim()}
          renderTags={(selectedOptions, getTagProps) => {
            const chips = selectedOptions.slice(0, 2).map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option[keyColumn]}
                className="item"
                label={valueFunc(option).trim()}
                disabled={disabled}
              />
            ));

            return (
              <Box sx={{ padding: "0px", display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {chips}
                {selectedOptions.length > 2 && (
                  <Chip label={`+${selectedOptions.length - 2} ${t("components.multiselect_dropdown_more")}`} />
                )}
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
                {...params}
                label={label}
                variant="outlined"    
                error={Boolean(error)}
                slotProps={{inputLabel: { shrink: true }}}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        minHeight: '42px', 
                        padding: '4px 8px', 
                    '& fieldset': {
                        borderColor: '#404CE2',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#007FFF',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: '#0066cc', 
                        borderWidth: 1,
                    }
                    }
                }}
            />
          )}
          
          renderOption={(props, option, { selected }) => (
              <Box
                  component="li"
                  {...props}
                  key={`option-${option[keyColumn]}`}
                  sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      fontWeight: selected ? "bold" : "normal",
                      backgroundColor: selected ? "rgba(25, 118, 210, 0.1) !important" : "transparent",
                      padding: '10px 16px',
                      "&:hover": { 
                          backgroundColor: "rgba(25, 118, 210, 0.2) !important", 
                      },
                  }}
              >
                  <Box sx={{ flexGrow: 1, paddingRight: '16px' }}>
                      {parentsUnselectable && option.disabled ? valueFunc(option) : valueFunc(option)}
                  </Box>
                  
                  <Box 
                      sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault(); 
                      }}
                      onTouchStart={(e) => {
                          e.stopPropagation();
                      }}
                  >
                      {option.descriptionFi && t("report.current_language") === "FINNISH" && (
                          <CompTooltipIcon ignore={true}>{option.descriptionFi}</CompTooltipIcon>
                      )}
                      
                      {option.descriptionSv && t("report.current_language") !== "FINNISH" && (
                          <CompTooltipIcon ignore={true}>{option.descriptionSv}</CompTooltipIcon>
                      )}
                  </Box>
              </Box>
          )}
        />

        <div className={`error-label ${props.className ? "error-" + props.className : ""}`}>
          {error}
        </div>
      </FormControl>
    );
};

SearchMultiselectDropdown.propTypes = {
  label: PropTypes.string,
  rows: PropTypes.array,
  selectedRows: PropTypes.array,
  error: PropTypes.string,
};

export { SearchMultiselectDropdown };
